import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  CogIcon,
  BanknotesIcon,
  CalculatorIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';
import { PayAnalysisEngine } from '../utils/PayAnalysisEngine';

const PayAnalysisPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  // State for data
  const [session, setSession] = useState(null);
  const [project, setProject] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  const [segments, setSegments] = useState([]);
  const [payTableName, setPayTableName] = useState(null);
  const [payAnalysis, setPayAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [correctionAnalysis, setCorrectionAnalysis] = useState(null);

  // UI state
  const [activeView, setActiveView] = useState('summary');

  useEffect(() => {
    if (sessionId) {
      fetchAnalysisData();
    }
  }, [sessionId]);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('prototype_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (sessionError) {
        throw new Error(`Session not found: ${sessionError.message}`);
      }
      setSession(sessionData);

      // Fetch project with configuration using the updated structure
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          project_configurations(
            *,
            pay_tables(id, name, description)
          )
        `)
        .eq('id', sessionData.project_id)
        .single();

      if (projectError) {
        throw new Error(`Project not found: ${projectError.message}`);
      }
      
      setProject(projectData);
      
      // Extract configuration - now using the updated structure
      let config = null;
      let payTable = null;
      
      if (projectData.project_configurations) {
        if (Array.isArray(projectData.project_configurations)) {
          config = projectData.project_configurations[0] || null;
        } else {
          config = projectData.project_configurations;
        }
        
        // Extract pay table from nested structure
        if (config && config.pay_tables) {
          payTable = config.pay_tables;
        }
      }
      
      
      setProjectConfig(config);

      // Check if project is properly configured
      if (!config?.is_configured) {
        setError('Project configuration is incomplete. Please configure the project before running pay analysis.');
        return;
      }

      if (!config?.pay_table_id || !payTable) {
        setError('No pay table assigned to this project. Please assign a pay table in project settings before running pay analysis.');
        return;
      }

      setPayTableName(payTable.name);

      // Fetch segments
      const { data: segmentsData, error: segmentsError } = await supabase
        .from('prototype_segments')
        .select('*')
        .eq('session_id', sessionId)
        .order('from_station_ft');

      if (segmentsError) {
        throw new Error(`Failed to load segments: ${segmentsError.message}`);
      }

      if (!segmentsData || segmentsData.length === 0) {
        setError('No segment data available for this session.');
        return;
      }

      setSegments(segmentsData);

      // Run pay analysis using the new engine
      await runPayAnalysis(segmentsData, payTable.name, config);

    } catch (err) {
      console.error('Error fetching analysis data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runPayAnalysis = async (segmentData, payTableName, configData) => {
    try {
      setCalculating(true);
      
      // Create analysis engine with new signature (segments, payTableName, config)
      const engine = new PayAnalysisEngine(segmentData, payTableName, configData);
      
      // Generate analysis using enhanced engine
      const results = engine.generatePayAnalysis();
      setPayAnalysis(results);

      // Get correction benefit analysis
      const correctionBenefit = engine.calculateCorrectionBenefit();
      setCorrectionAnalysis(correctionBenefit);

      
    } catch (err) {
      console.error('Error running pay analysis:', err);
      setError('Failed to calculate pay analysis: ' + err.message);
    } finally {
      setCalculating(false);
    }
  };

  const handleExportAnalysis = async () => {
    if (!payAnalysis || !segments || !payTableName || !projectConfig) return;

    try {
      const engine = new PayAnalysisEngine(segments, payTableName, projectConfig);
      const exportData = engine.generateExportData();
      
      // Create CSV content
      const csvHeaders = [
        'Lot Number', 'Start Station (ft)', 'End Station (ft)', 'Length (ft)', 
        'MRI (in/mi)', 'Pay Factor', 'Tonnage', 'Base Payment ($)', 
        'Adjusted Payment ($)', 'Dollar Adjustment ($)', 'Requires Correction',
        'Target MRI', 'Note'
      ];
      
      const csvRows = exportData.lots_data.map(lot => [
        lot.lot_number,
        lot.start_station_ft.toFixed(2),
        lot.end_station_ft.toFixed(2),
        lot.length_ft.toFixed(2),
        lot.mri_in_per_mi.toFixed(2),
        lot.pay_factor.toFixed(3),
        lot.tonnage.toFixed(2),
        lot.base_payment.toFixed(2),
        lot.adjusted_payment.toFixed(2),
        lot.dollar_adjustment.toFixed(2),
        lot.requires_correction ? 'Yes' : 'No',
        lot.target_mri || '',
        lot.note || ''
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(cell => 
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name}_${session?.filename}_PayAnalysis.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting analysis:', err);
      alert('Failed to export data: ' + err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatStation = (station) => {
    if (!station) return 'N/A';
    const hundreds = Math.floor(station / 100);
    const remainder = station % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  };

  const getPayFactorColor = (payFactor) => {
    if (payFactor > 1.0) return 'text-green-600';
    if (payFactor < 1.0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getConditionColor = (mri) => {
    if (mri <= 35) return '#00C853';   // Excellent
    if (mri <= 60) return '#4CAF50';   // Good
    if (mri <= 85) return '#FFEB3B';   // Fair
    if (mri <= 110) return '#FF9800';  // Poor
    return '#F44336';                  // Very Poor
  };

  // Enhanced Summary Cards Component
  const SummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {/* Base Payment */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <BanknotesIcon className="h-8 w-8 text-gray-600 mr-3" />
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(payAnalysis?.summary?.totalBasePayment || 0)}
            </div>
            <div className="text-sm text-gray-500">Base Payment</div>
          </div>
        </div>
      </div>

      {/* Adjustment */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <CalculatorIcon className="h-8 w-8 text-blue-600 mr-3" />
          <div>
            <div className={`text-2xl font-bold ${
              (payAnalysis?.summary?.totalDollarAdjustment || 0) > 0 ? 'text-green-600' : 
              (payAnalysis?.summary?.totalDollarAdjustment || 0) < 0 ? 'text-red-600' : 'text-gray-900'
            }`}>
              {(payAnalysis?.summary?.totalDollarAdjustment || 0) >= 0 ? '+' : ''}
              {formatCurrency(payAnalysis?.summary?.totalDollarAdjustment || 0)}
            </div>
            <div className="text-sm text-gray-500">Adjustment</div>
          </div>
        </div>
      </div>

      {/* Net Payment */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(payAnalysis?.summary?.totalAdjustedPayment || 0)}
            </div>
            <div className="text-sm text-gray-500">Net Payment</div>
          </div>
        </div>
      </div>

      {/* Avg Pay Factor */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <ChartBarIcon className="h-8 w-8 text-purple-600 mr-3" />
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {payAnalysis?.summary?.avgPayFactor?.toFixed(3) || 'N/A'}
            </div>
            <div className="text-sm text-gray-500">Avg Pay Factor</div>
          </div>
        </div>
      </div>

      {/* Bonus Lots */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
          <div>
            <div className="text-2xl font-bold text-green-600">
              {payAnalysis?.summary?.bonusLots || 0}
            </div>
            <div className="text-sm text-gray-500">Bonus Lots</div>
          </div>
        </div>
      </div>

      {/* Penalty Lots */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3" />
          <div>
            <div className="text-2xl font-bold text-red-600">
              {payAnalysis?.summary?.penaltyLots || 0}
            </div>
            <div className="text-sm text-gray-500">Penalty Lots</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced Pay Factor Chart Component
  const PayFactorChart = () => {
    const chartData = payAnalysis?.lots?.map((lot, index) => ({
      lot: `Lot ${lot.lotNumber}`,
      station: formatStation(lot.startStation),
      payFactor: lot.payFactor,
      dollarAdjustment: lot.dollarAdjustment,
      mri: lot.mri,
      requiresCorrection: lot.requiresCorrection
    })) || [];

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pay Factors by Lot</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="station" 
              label={{ value: 'Station', position: 'insideBottom', offset: -5 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              label={{ value: 'Pay Factor', angle: -90, position: 'insideLeft' }}
              domain={[0.9, 1.1]}
            />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'payFactor') return [value.toFixed(3), 'Pay Factor'];
                return [value, name];
              }}
              labelFormatter={(label) => `Station: ${label}`}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-300 rounded shadow">
                      <p className="font-medium">{`Station: ${label}`}</p>
                      <p className="text-blue-600">{`Pay Factor: ${data.payFactor.toFixed(3)}`}</p>
                      <p className="text-gray-600">{`MRI: ${data.mri.toFixed(1)} in/mi`}</p>
                      <p className="text-green-600">{`Adjustment: ${formatCurrency(data.dollarAdjustment)}`}</p>
                      {data.requiresCorrection && (
                        <p className="text-red-600 text-xs">⚠️ Requires Correction</p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="payFactor" name="Pay Factor">
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={
                    entry.requiresCorrection ? '#dc2626' :  // Red for correction required
                    entry.payFactor > 1.0 ? '#16a34a' :    // Green for bonus
                    entry.payFactor < 1.0 ? '#f97316' :    // Orange for penalty
                    '#6b7280'                              // Gray for neutral
                  } 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Enhanced Lot Results Table Component
  const LotResultsTable = () => (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Lot-by-Lot Analysis</h3>
        <p className="text-sm text-gray-600 mt-1">
          Detailed payment calculations for each {projectConfig?.analysis_segment_length_ft || 528} ft lot using {payTableName} pay table
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lot #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Start Station
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                End Station
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Length (ft)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                MRI (in/mi)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pay Factor
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tonnage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Base Payment
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Adjusted Payment
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Adjustment
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payAnalysis?.lots?.map((lot, index) => (
              <tr key={lot.lotNumber} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {lot.lotNumber}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                  {formatStation(lot.startStation)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                  {formatStation(lot.endStation)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {lot.lengthFt.toFixed(0)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" style={{ color: getConditionColor(lot.mri) }}>
                  {lot.mri.toFixed(1)}
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-sm font-bold ${getPayFactorColor(lot.payFactor)}`}>
                  {lot.payFactor.toFixed(3)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {lot.tonnage.toFixed(2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(lot.basePayment)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(lot.adjustedPayment)}
                </td>
                <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${
                  lot.dollarAdjustment > 0 ? 'text-green-600' : 
                  lot.dollarAdjustment < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {lot.dollarAdjustment >= 0 ? '+' : ''}{formatCurrency(lot.dollarAdjustment)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {lot.requiresCorrection ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <WrenchScrewdriverIcon className="h-3 w-3 mr-1" />
                      Correction Required
                    </span>
                  ) : lot.payCategory === 'bonus' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Bonus
                    </span>
                  ) : lot.payCategory === 'penalty' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Penalty
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Neutral
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // New Correction Analysis Component
  const CorrectionAnalysis = () => {
    if (!correctionAnalysis || correctionAnalysis.lots_requiring_correction === 0) {
      return null;
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <div className="flex">
          <WrenchScrewdriverIcon className="h-5 w-5 text-yellow-600 mr-2" />
          <div>
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Correction Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-yellow-700">Lots Requiring Correction</div>
                <div className="font-bold text-yellow-900">{correctionAnalysis.lots_requiring_correction}</div>
              </div>
              <div>
                <div className="text-yellow-700">Current Penalty</div>
                <div className="font-bold text-red-600">{formatCurrency(correctionAnalysis.current_total_penalty)}</div>
              </div>
              <div>
                <div className="text-yellow-700">Potential Savings</div>
                <div className="font-bold text-green-600">{formatCurrency(correctionAnalysis.potential_total_savings)}</div>
              </div>
              <div>
                <div className="text-yellow-700">Net Benefit</div>
                <div className="font-bold text-blue-600">{formatCurrency(correctionAnalysis.net_benefit)}</div>
              </div>
            </div>
            <p className="text-xs text-yellow-700 mt-2">
              Corrective action could improve payment by {formatCurrency(correctionAnalysis.net_benefit)} if all flagged lots are brought to target MRI levels.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading pay analysis data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Pay Analysis Unavailable</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            {error.includes('configuration') && (
              <button
                onClick={() => navigate(`/projects/${project?.id}?tab=settings`)}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <CogIcon className="h-4 w-4 mr-2" />
                Configure Project
              </button>
            )}
            <button
              onClick={() => navigate(`/session/${sessionId}`)}
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/session/${sessionId}`)}
                className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Session
              </button>
              <div className="h-5 border-l border-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Pay Analysis</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {project?.name} | {session?.filename || 'Unknown File'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExportAnalysis}
                disabled={!payAnalysis}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export Analysis
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {calculating ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Calculating pay adjustments using {payTableName} specifications...</p>
          </div>
        ) : payAnalysis ? (
          <div className="space-y-6">
            {/* Enhanced Configuration Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Analysis Configuration</p>
                  <p className="text-blue-700 mt-1">
                    {payAnalysis.summary.totalLots} lots of {projectConfig?.analysis_segment_length_ft || 528} ft each • 
                    Pay Table: {payTableName} • 
                    Unit Price: {formatCurrency(projectConfig?.unit_price_per_ton || 0)}/ton • 
                    Spread Rate: {projectConfig?.spread_rate_lbs_sy || 0} lbs/SY • 
                    Pavement Type: {projectConfig?.pavement_type || 'A'} •
                    Project Compliance: {payAnalysis.summary.projectCompliance}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <SummaryCards />

            {/* Correction Analysis */}
            <CorrectionAnalysis />

            {/* Charts */}
            <PayFactorChart />

            {/* Detailed Results Table */}
            <LotResultsTable />

            {/* Enhanced Summary Statistics */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Total Length</div>
                  <div className="font-medium">{payAnalysis.summary.totalLengthMiles.toFixed(2)} miles</div>
                </div>
                <div>
                  <div className="text-gray-500">Total Tonnage</div>
                  <div className="font-medium">{payAnalysis.summary.totalTonnage.toFixed(1)} tons</div>
                </div>
                <div>
                  <div className="text-gray-500">Average MRI</div>
                  <div className="font-medium">{payAnalysis.summary.avgMri.toFixed(1)} in/mi</div>
                </div>
                <div>
                  <div className="text-gray-500">Overall Quality</div>
                  <div className="font-medium">{payAnalysis.summary.overallQuality}</div>
                </div>
                <div>
                  <div className="text-gray-500">Pay Factor Range</div>
                  <div className="font-medium">
                    {payAnalysis.summary.minPayFactor.toFixed(3)} - {payAnalysis.summary.maxPayFactor.toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Base Payment</div>
                  <div className="font-medium">{formatCurrency(payAnalysis.summary.totalBasePayment)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Adjusted Payment</div>
                  <div className="font-medium">{formatCurrency(payAnalysis.summary.totalAdjustedPayment)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Net Adjustment</div>
                  <div className={`font-medium ${
                    payAnalysis.summary.totalDollarAdjustment > 0 ? 'text-green-600' : 
                    payAnalysis.summary.totalDollarAdjustment < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {payAnalysis.summary.totalDollarAdjustment >= 0 ? '+' : ''}
                    {formatCurrency(payAnalysis.summary.totalDollarAdjustment)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Correction Required</div>
                  <div className="font-medium text-red-600">
                    {payAnalysis.summary.correctionRequiredLots} lots ({payAnalysis.summary.correctionPercentage.toFixed(1)}%)
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">MRI Std Deviation</div>
                  <div className="font-medium">{payAnalysis.summary.mriStandardDeviation.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Project Compliance</div>
                  <div className="font-medium capitalize">{payAnalysis.summary.projectCompliance.replace('_', ' ')}</div>
                </div>
                <div>
                  <div className="text-gray-500">Analysis Engine</div>
                  <div className="font-medium">{payAnalysis.metadata.pay_table}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Results</h3>
            <p className="text-gray-500">Unable to generate pay analysis</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayAnalysisPage;